import React from 'react';
import { Helmet } from 'react-helmet-async';

export const AboutUs: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>About Us - Our Mission | Chinese Food Maps</title>
        <meta name="description" content="Meet the team behind Chinese Food Maps and discover our mission to connect food lovers with authentic Chinese cuisine. Learn about our journey, values, and commitment to helping you find the best local Chinese restaurants." />
        <meta property="og:title" content="About Us - Our Mission | Chinese Food Maps" />
        <meta property="og:description" content="Meet the team behind Chinese Food Maps and discover our mission to connect food lovers with authentic Chinese cuisine." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://chinesefoodmaps.com/about" />
        <meta property="og:image" content="https://cdn-icons-png.flaticon.com/512/2548/2548242.png" />
        <link rel="canonical" href="https://chinesefoodmaps.com/about" />
      </Helmet>
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-6 text-red-900">About Chinese Food Maps</h1>
      
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="prose max-w-none text-gray-600 space-y-6">
            <p className="text-lg leading-relaxed">
              Welcome to Chinese Food Maps, your trusted companion in discovering authentic Chinese cuisine. Founded in 2025, we've made it our mission to connect food enthusiasts with exceptional Chinese restaurants, making it easier than ever to explore the rich tapestry of Chinese culinary traditions.
            </p>
            
            <p className="text-lg leading-relaxed">
              Our platform celebrates the incredible diversity of Chinese cuisine, from traditional family-owned establishments serving generations-old recipes to modern restaurants innovating with regional flavors. We understand that Chinese cuisine is not monolithic - each region has its unique specialties, cooking techniques, and flavor profiles that contribute to China's vast culinary heritage.
            </p>

            <h2 className="text-2xl font-semibold text-red-800 mt-8 mb-4">Our Story</h2>
            <p className="text-lg leading-relaxed">
              Chinese Food Maps was born from a deep passion for authentic Chinese cuisine and a recognition of the challenges many face in finding genuine regional Chinese dishes. Our founder, growing up in a family of Chinese food enthusiasts, noticed how difficult it was for people to discover authentic Chinese restaurants beyond the familiar Americanized offerings. This inspired the creation of a platform that would help people explore the true diversity of Chinese cuisine.
            </p>

            <h2 className="text-2xl font-semibold text-red-800 mt-8 mb-4">Our Mission</h2>
            <p className="text-lg leading-relaxed">
              We believe that authentic Chinese food should be accessible to everyone. Our mission is to:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-6">
              <li>Connect people with authentic Chinese restaurants in their area</li>
              <li>Promote understanding of China's eight major culinary traditions</li>
              <li>Help Chinese restaurants reach more customers</li>
              <li>Educate users about different Chinese dishes and cooking techniques</li>
              <li>Build a community of Chinese food enthusiasts</li>
            </ul>

            <h2 className="text-2xl font-semibold text-red-800 mt-8 mb-4">Why Choose Chinese Food Maps?</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-red-50 p-6 rounded-lg">
                <h3 className="text-xl font-semibold mb-3 text-red-800">Comprehensive Coverage</h3>
                <p>We maintain detailed information about Chinese restaurants, including regional specialties, hours, contact information, and user reviews.</p>
              </div>
              <div className="bg-red-50 p-6 rounded-lg">
                <h3 className="text-xl font-semibold mb-3 text-red-800">Regional Expertise</h3>
                <p>Our platform helps you discover restaurants specializing in specific Chinese regional cuisines, from Cantonese dim sum to Sichuan hot pot.</p>
              </div>
              <div className="bg-red-50 p-6 rounded-lg">
                <h3 className="text-xl font-semibold mb-3 text-red-800">Educational Resources</h3>
                <p>Learn about different Chinese dishes, cooking techniques, and regional specialties through our educational content.</p>
              </div>
              <div className="bg-red-50 p-6 rounded-lg">
                <h3 className="text-xl font-semibold mb-3 text-red-800">Community Focus</h3>
                <p>We're building a community of Chinese food enthusiasts who share recommendations and experiences.</p>
              </div>
            </div>

            <p className="text-lg leading-relaxed mt-8">
              Beyond just listings, we provide comprehensive information about each establishment, including ratings from real customers, easy access to directions, and details about signature dishes. Our platform is designed to celebrate the artistry of Chinese cooking while making great Chinese food accessible to everyone.
            </p>

            <div className="bg-red-50 rounded-lg p-6 mt-8">
              <h3 className="text-xl font-semibold text-red-800 mb-4">Our Commitment to Authenticity</h3>
              <p className="text-lg leading-relaxed">
                We are dedicated to promoting authentic Chinese cuisine and helping preserve traditional cooking methods and flavors. Our team works closely with restaurant owners and chefs to ensure accurate representation of their regional specialties and cooking techniques. We believe that understanding the rich history and cultural significance of Chinese cuisine enhances the dining experience and helps build bridges between communities.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};